// Pinned sessions: a list of session ids kept in this browser (localStorage "cmx.pins"). The hub has
// no place for it yet (account settings are a fixed set of keys); when it does, this is the one file
// to move. A pinned session goes first in the sidebar, in its group, and ⌘1–9 follow.

import { useSyncExternalStore } from 'react';
import { store } from '../store';

const KEY = 'cmx.pins';

function read(): number[] {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(KEY) ?? '[]');
    return Array.isArray(v) ? v.filter((n): n is number => Number.isInteger(n)) : [];
  } catch {
    return [];
  }
}

let pins: number[] = read();
const listeners = new Set<() => void>();

function set(next: number[]) {
  if (next.length === pins.length && next.every((id, i) => id === pins[i])) return;
  pins = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(pins));
  } catch {
    // private window or blocked storage: pins last for this visit
  }
  listeners.forEach((l) => l());
  // lists derived from the sessions (⌘1–9, the palette, the empty pane's picker) take the new order
  store.set({ sessions: [...store.get().sessions] });
}

export const getPins = (): readonly number[] => pins;
export const isPinned = (id: number) => pins.includes(id);
export const togglePin = (id: number) => set(isPinned(id) ? pins.filter((p) => p !== id) : [...pins, id]);
/** Forgets pins of sessions that are gone. */
export const prunePins = (live: number[]) => set(pins.filter((id) => live.includes(id)));

const subscribe = (l: () => void) => {
  listeners.add(l);
  const onStorage = (e: StorageEvent) => {
    if (e.key !== KEY) return;
    pins = read();
    l();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(l);
    window.removeEventListener('storage', onStorage);
  };
};
export const usePins = () => useSyncExternalStore(subscribe, getPins);
